/*
  Shared carousel behaviour for Categories, Pain points and Reviews.
  Markup (see snippets/rh-carousel-controls.liquid):
    <div class="rh-carousel" data-rh-carousel>
      <div class="rh-carousel__track" data-rh-carousel-track id="...">cards</div>
      <div data-rh-carousel-nav hidden><button data-rh-carousel-prev> <button data-rh-carousel-next></div>
    </div>
  - Arrows show only when the cards do not all fit (scrollWidth > clientWidth); checked again on resize, on image
    load and on theme editor events. At the first / last card the arrow gets aria-disabled="true" (no loop).
  - Keyboard: the arrow buttons, and Left / Right on the track (the track is focusable while it scrolls).
  - Touch swipe and trackpad: native horizontal scrolling with scroll-snap. Mouse: drag to scroll.
  - Root attributes for styling: data-overflow, data-at-start, data-at-end ("true" / "false").
  Editor-safe: state is stored on the root element (root.__rhCarousel); every optional element is null-checked;
  the document listeners are added once (guarded by window.RhCarousel, the public API: RhCarousel.init(root),
  RhCarousel.update(root)). Several <script> tags for this file on one page are harmless.
*/
(function () {
  'use strict';
  if (window.RhCarousel) return;

  var ROOT = '[data-rh-carousel]';

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function visibleItems(track) {
    var out = [];
    for (var i = 0; i < track.children.length; i++) {
      var c = track.children[i];
      if (c.getClientRects().length) out.push(c);
    }
    return out;
  }

  function gap(track) {
    var s = window.getComputedStyle(track);
    return parseFloat(s.columnGap) || parseFloat(s.gap) || 0;
  }

  function init(root) {
    if (!root || root.__rhCarousel) return;
    var track = root.querySelector('[data-rh-carousel-track]');
    if (!track) return;
    var nav = root.querySelector('[data-rh-carousel-nav]');
    var prev = root.querySelector('[data-rh-carousel-prev]');
    var next = root.querySelector('[data-rh-carousel-next]');
    var st = { track: track, raf: 0, drag: null, suppressClick: false, observer: null };
    root.__rhCarousel = st;

    function maxScroll() { return Math.max(0, track.scrollWidth - track.clientWidth); }
    function overflows() { return track.scrollWidth - track.clientWidth > 1; }
    function setDisabled(btn, off) { if (btn) btn.setAttribute('aria-disabled', off ? 'true' : 'false'); }

    function update() {
      if (st.raf) { window.cancelAnimationFrame(st.raf); st.raf = 0; }
      if (!root.isConnected) return;
      var over = overflows();
      var x = Math.abs(track.scrollLeft);
      var atStart = !over || x <= 1;
      var atEnd = !over || x >= maxScroll() - 1;
      root.setAttribute('data-overflow', over ? 'true' : 'false');
      root.setAttribute('data-at-start', atStart ? 'true' : 'false');
      root.setAttribute('data-at-end', atEnd ? 'true' : 'false');
      if (nav) nav.hidden = !over;
      setDisabled(prev, atStart);
      setDisabled(next, atEnd);
      if (over) {
        if (!track.hasAttribute('tabindex')) { track.setAttribute('tabindex', '0'); st.ownTabindex = true; }
      } else if (st.ownTabindex) {
        track.removeAttribute('tabindex'); st.ownTabindex = false;
      }
    }
    /* rAF for resize bursts; the timeout also covers hidden tabs where rAF does not run */
    function schedule() {
      if (st.raf) return;
      st.raf = window.requestAnimationFrame(update);
      window.setTimeout(update, 150);
    }
    st.update = update;

    /* One card per step (card width + gap), so the snap lands on the next card. */
    function stepSize() {
      var items = visibleItems(track);
      if (!items.length) return track.clientWidth;
      return items[0].getBoundingClientRect().width + gap(track);
    }
    function go(dir) {
      if (!overflows()) return;
      var target = Math.min(maxScroll(), Math.max(0, track.scrollLeft + dir * stepSize()));
      track.scrollTo({ left: target, behavior: reducedMotion() ? 'instant' : 'smooth' });
      settle();
    }
    /* Scroll events update the arrows; these timers are a safety net for browsers that batch them */
    function settle() { window.setTimeout(update, 60); window.setTimeout(update, 700); }
    st.go = go;

    /* The snap position of the card nearest to the current scroll position (used after a mouse drag). */
    function snapNearest() {
      var items = visibleItems(track);
      if (!items.length) return;
      var tr = track.getBoundingClientRect();
      var pad = parseFloat(window.getComputedStyle(track).scrollPaddingLeft) || 0;
      var best = null, bestDist = Infinity;
      items.forEach(function (it) {
        var r = it.getBoundingClientRect();
        var align = window.getComputedStyle(it).scrollSnapAlign || '';
        var delta = align.indexOf('center') > -1 ? (r.left + r.width / 2) - (tr.left + tr.width / 2) : r.left - tr.left - pad;
        if (Math.abs(delta) < bestDist) { bestDist = Math.abs(delta); best = track.scrollLeft + delta; }
      });
      if (best !== null) {
        track.scrollTo({ left: Math.min(maxScroll(), Math.max(0, best)), behavior: reducedMotion() ? 'instant' : 'smooth' });
        settle();
      }
    }

    function onArrow(dir) {
      return function (e) {
        e.preventDefault();
        var btn = e.currentTarget;
        if (btn.getAttribute('aria-disabled') === 'true') return;
        go(dir);
      };
    }
    if (prev) prev.addEventListener('click', onArrow(-1));
    if (next) next.addEventListener('click', onArrow(1));

    track.addEventListener('scroll', function () { update(); }, { passive: true });
    track.addEventListener('scrollend', function () { update(); });

    track.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      var t = e.target;
      if (t && t.closest && t.closest('input, textarea, select, [contenteditable]')) return;
      if (!overflows()) return;
      e.preventDefault();
      go(e.key === 'ArrowRight' ? 1 : -1);
    });

    /* Mouse drag. Touch and pen keep the native swipe. */
    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0 || !overflows()) return;
      st.drag = { id: e.pointerId, x: e.clientX, left: track.scrollLeft, moved: false };
    });
    track.addEventListener('pointermove', function (e) {
      var d = st.drag;
      if (!d || e.pointerId !== d.id) return;
      var dx = e.clientX - d.x;
      if (!d.moved) {
        if (Math.abs(dx) < 6) return;
        d.moved = true;
        track.classList.add('is-dragging');
        try { track.setPointerCapture(e.pointerId); } catch (err) { /* not supported: drag still works inside the track */ }
      }
      track.scrollLeft = d.left - dx;
      e.preventDefault();
    });
    function endDrag(e) {
      var d = st.drag;
      if (!d || (e && e.pointerId !== d.id)) return;
      st.drag = null;
      try { track.releasePointerCapture(d.id); } catch (err) { /* already released */ }
      if (!d.moved) return;
      track.classList.remove('is-dragging');
      st.suppressClick = true;
      window.setTimeout(function () { st.suppressClick = false; }, 80);
      snapNearest();
    }
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    /* A drag must not open the card link underneath */
    track.addEventListener('click', function (e) {
      if (!st.suppressClick) return;
      st.suppressClick = false;
      e.preventDefault();
      e.stopPropagation();
    }, true);
    /* No native image / link drag ghost while the cards scroll */
    track.addEventListener('dragstart', function (e) { if (overflows()) e.preventDefault(); });

    /* Images that load later can change the card size */
    Array.prototype.forEach.call(track.querySelectorAll('img'), function (img) {
      if (!img.complete) img.addEventListener('load', schedule, { once: true });
    });

    if ('ResizeObserver' in window) {
      st.observer = new ResizeObserver(schedule);
      st.observer.observe(track);
      visibleItems(track).forEach(function (it) { st.observer.observe(it); });
    }
    /* After an editor reload the section's own stylesheet link loads again: measure once it is there */
    var section = root.closest('.shopify-section') || root;
    Array.prototype.forEach.call(section.querySelectorAll('link[rel="stylesheet"]'), function (link) {
      link.addEventListener('load', schedule, { once: true });
    });
    update();
    window.setTimeout(update, 300);
  }

  function update(root) {
    var st = root && root.__rhCarousel;
    if (st && st.update) st.update();
  }

  function destroy(root) {
    var st = root && root.__rhCarousel;
    if (!st) return;
    if (st.observer) st.observer.disconnect();
    if (st.raf) window.cancelAnimationFrame(st.raf);
    root.__rhCarousel = null;
  }

  function each(scope, fn) {
    if (!scope || !scope.querySelectorAll) return;
    if (scope.matches && scope.matches(ROOT)) fn(scope);
    Array.prototype.forEach.call(scope.querySelectorAll(ROOT), fn);
  }

  window.RhCarousel = { init: init, update: update, destroy: destroy };

  function boot() { each(document, init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* Fonts and late images can change sizes after the first check */
  window.addEventListener('load', function () { each(document, update); });
  if (!('ResizeObserver' in window)) {
    window.addEventListener('resize', function () { each(document, update); });
  }

  /* Theme editor */
  document.addEventListener('shopify:section:load', function (e) { each(e.target, init); });
  document.addEventListener('shopify:section:unload', function (e) { each(e.target, destroy); });
  document.addEventListener('shopify:block:select', function (e) {
    var block = e.target;
    var track = block && block.closest ? block.closest('[data-rh-carousel-track]') : null;
    if (!track) return;
    var root = track.closest(ROOT);
    var tr = track.getBoundingClientRect();
    var r = block.getBoundingClientRect();
    if (r.left < tr.left || r.right > tr.right) {
      track.scrollTo({ left: track.scrollLeft + (r.left - tr.left) - (tr.width - r.width) / 2, behavior: 'instant' });
    }
    update(root);
  });
  document.addEventListener('shopify:block:deselect', function (e) {
    var track = e.target && e.target.closest ? e.target.closest('[data-rh-carousel-track]') : null;
    if (track) update(track.closest(ROOT));
  });
})();
